import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Typography,
  Container,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import { tokens, shadows } from '../../theme';
import { INTEGRATION_STATUS, SECURITY_POSTURE, TRANSACTION_DEFINITION } from '../../content/publicClaims';

const FAQSection: React.FC = () => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: 'How does the free tier work?',
      answer: 'Start on the free tier. No credit card, and it does not expire. It covers 100 transactions a month, one user and one integration. Subscribe when you need more than that.',
    },
    {
      question: 'What integrations do you support?',
      answer: INTEGRATION_STATUS,
    },
    {
      question: 'How is this different from Google Analytics or Shopify reports?',
      answer: 'We read the accounts you connect. Clover and Stripe are the two with sync paths we have verified. Google Analytics connects, but nothing in the product can trigger its sync, so it brings in no data either. QuickBooks connects but does not sync at all yet, so it brings in nothing. That is the list, not all your data sources. Over that history we run anomaly detection and project revenue sixty days out. We do not tell you why something happened: root cause analysis is not built, so we are not going to claim it.',
    },
    {
      question: 'Is my data secure?',
      answer: SECURITY_POSTURE,
    },
    {
      question: 'What\'s the difference between AI insights and human consulting?',
      answer: 'Clover and Stripe each pull when you run a sync from their own card on the Integrations page. Anomaly detection runs over that sales history when you generate insights on the dashboard. It is not instant and it is not always on. Human consultants bring the context the software has no way to see, which is most of what matters in a decision.',
    },
    {
      question: 'Can I change or cancel my plan anytime?',
      answer: 'Yes! You can upgrade, downgrade, or cancel your plan at any time. When upgrading, you get immediate access to new features. When downgrading or canceling, changes take effect at the end of your current billing cycle.',
    },
    {
      question: 'What counts as a transaction?',
      answer: TRANSACTION_DEFINITION,
    },
    {
      question: 'Do you offer custom enterprise solutions?',
      answer: 'The White Glove tier removes the usage limits and adds consulting hours, quarterly reviews and a direct line to the person building it. It does not include custom integrations: we have built Clover and Stripe, and building a new connector is not something we are currently selling. Contact us and we will tell you honestly whether we can help.',
    },
  ];

  const handleToggle = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        background: tokens.background.gradient,
        position: 'relative',
      }}
    >
      <Container maxWidth="md">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Typography
              variant="overline"
              sx={{
                color: tokens.primary.main,
                fontWeight: 700,
                letterSpacing: '0.1em',
                mb: 2,
                display: 'block',
              }}
            >
              FAQ
            </Typography>

            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                color: tokens.text.primary,
                mb: 2,
                fontSize: { xs: '2rem', md: '2.75rem' },
                letterSpacing: '-0.03em',
              }}
            >
              Frequently Asked{' '}
              <Box
                component="span"
                sx={{
                  background: tokens.primary.gradient,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Questions
              </Box>
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: tokens.text.secondary,
                maxWidth: 500,
                mx: 'auto',
              }}
            >
              Everything you need to know about Chairs Up.
            </Typography>
          </Box>
        </motion.div>

        {/* FAQ Accordion */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
            >
              <Box
                onClick={() => handleToggle(index)}
                sx={{
                  background: tokens.background.paper,
                  borderRadius: '16px',
                  border: `1px solid ${expandedIndex === index ? alpha(tokens.primary.main, 0.3) : tokens.border.light}`,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: expandedIndex === index ? shadows.lg : 'none',
                  '&:hover': {
                    borderColor: alpha(tokens.primary.main, 0.3),
                  },
                }}
              >
                {/* Question */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 3,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: tokens.text.primary,
                      pr: 2,
                    }}
                  >
                    {faq.question}
                  </Typography>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '8px',
                      background: expandedIndex === index
                        ? tokens.primary.gradient
                        : alpha(tokens.primary.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {expandedIndex === index ? (
                      <RemoveIcon sx={{ fontSize: 18, color: 'white' }} />
                    ) : (
                      <AddIcon sx={{ fontSize: 18, color: tokens.primary.main }} />
                    )}
                  </Box>
                </Box>

                {/* Answer */}
                <AnimatePresence>
                  {expandedIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Box
                        sx={{
                          px: 3,
                          pb: 3,
                          pt: 0,
                        }}
                      >
                        <Box
                          sx={{
                            height: 1,
                            background: tokens.border.light,
                            mb: 2,
                          }}
                        />
                        <Typography
                          variant="body1"
                          sx={{
                            color: tokens.text.secondary,
                            lineHeight: 1.8,
                          }}
                        >
                          {faq.answer}
                        </Typography>
                      </Box>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Box>
            </motion.div>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default FAQSection;
